import React from "react";
import {
  Box,
  Heading,
  Link,
  Image,
  Text,
  Divider,
  HStack,
  Tag,
  Wrap,
  WrapItem,
  useColorModeValue,
  Container,
  VStack,
  ChakraProvider,
  theme,
  Center,
  VisuallyHidden,
} from "@chakra-ui/react";

import giglio from "./data/img/giglio.jpg";
import crop_field from "./data/img/crop_field_jvh.jpg";
import medal from "./data/img/NASA_OPLM.jpg";
import zubkova from "./data/img/maria.jpg";
import hall from "./data/img/joanne.jpg";
import roy from "./data/img/roy.jpg";
import humber from "./data/img/mike.jpg";

// Accessible BlogTags component with proper list semantics
const BlogTags = ({ tags, marginTop }) => (
  <HStack
    spacing={2}
    marginTop={marginTop}
    as="ul"
    listStyleType="none"
    aria-label="Article tags"
  >
    {tags.map((tag) => (
      <Tag
        as="li"
        size="md"
        variant="solid"
        colorScheme="orange"
        key={tag}
      >
        {tag}
      </Tag>
    ))}
  </HStack>
);

const authorImages = {
  "L. Giglio": giglio,
  "M. Zubkova": zubkova,
  "J. V. Hall": hall,
  "D. Roy": roy,
  "M. Humber": humber,
};

// Accessible BlogAuthor component with proper date formatting
export const BlogAuthor = ({ name, date }) => {
  const authorImage = authorImages[name] || giglio;
  const formattedDate = date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const isoDate = date.toISOString().split("T")[0];

  return (
    <HStack marginTop="2" spacing="2" display="flex" alignItems="center">
      <Image
        borderRadius="full"
        height="45px"
        width="35px"
        src={authorImage}
        alt="" // Decorative image, name is provided in text
        aria-hidden="true"
      />
      <Text fontWeight="medium">{name}</Text>
      <Text aria-hidden="true">—</Text>
      <Text as="time" dateTime={isoDate}>
        {formattedDate}
      </Text>
    </HStack>
  );
};

// Reusable NewsArticle component for better maintainability and accessibility
const NewsArticle = ({
  href,
  imageSrc,
  imageAlt,
  tags,
  title,
  description,
  authorName,
  authorDate,
  headingLevel = "h2",
}) => {
  const bgGradient = useColorModeValue(
    "radial(orange.600 1px, transparent 1px)",
    "radial(orange.300 1px, transparent 1px)"
  );
  const textColor = useColorModeValue("gray.700", "gray.200");
  const linkHoverColor = useColorModeValue("blue.700", "blue.300");
  const focusOutlineColor = useColorModeValue("blue.500", "blue.300");

  return (
    <Box
      as="article"
      marginTop={{ base: "4", sm: "5" }}
      display="flex"
      flexDirection={{ base: "column", sm: "row" }}
      justifyContent="space-between"
      aria-labelledby={`article-title-${title.replace(/\s+/g, "-").toLowerCase().slice(0, 30)}`}
    >
      {/* Image Section */}
      <Box
        display="flex"
        flex="1"
        marginRight={{ base: "0", sm: "3" }}
        marginBottom={{ base: "4", sm: "0" }}
        position="relative"
        alignItems="center"
        minHeight={{ base: "200px", sm: "auto" }}
      >
        <Center
          width={{ base: "100%", sm: "85%" }}
          zIndex="2"
          marginLeft={{ base: "0", sm: "5%" }}
          marginTop={{ base: "0", sm: "5%" }}
        >
          <Link
            href={href}
            isExternal
            display="block"
            _hover={{ opacity: 0.9 }}
            _focus={{
              outline: "3px solid",
              outlineColor: focusOutlineColor,
              outlineOffset: "3px",
            }}
            aria-label={`View article: ${title} (opens in new tab)`}
          >
            <Image
              borderRadius="sm"
              src={imageSrc}
              alt={imageAlt}
              objectFit="contain"
              maxHeight={{ base: "250px", sm: "300px", md: "350px" }}
              width="100%"
            />
          </Link>
        </Center>
        {/* Decorative background pattern */}
        <Box
          zIndex="1"
          width="100%"
          position="absolute"
          height="100%"
          aria-hidden="true"
        >
          <Box
            bgGradient={bgGradient}
            backgroundSize="20px 20px"
            opacity="0.4"
            height="100%"
          />
        </Box>
      </Box>

      {/* Content Section */}
      <Box
        display="flex"
        flex="1"
        flexDirection="column"
        justifyContent="center"
        marginTop={{ base: "3", sm: "0" }}
      >
        <BlogTags tags={tags} />
        <Heading
          as={headingLevel}
          size="lg"
          marginTop="1"
          id={`article-title-${title.replace(/\s+/g, "-").toLowerCase().slice(0, 30)}`}
        >
          <Link
            href={href}
            isExternal
            color={useColorModeValue("gray.800", "white")}
            textDecoration="none"
            _hover={{
              textDecoration: "underline",
              color: linkHoverColor,
            }}
            _focus={{
              outline: "2px solid",
              outlineColor: focusOutlineColor,
              outlineOffset: "2px",
            }}
          >
            {title}
            <VisuallyHidden> (opens in new tab)</VisuallyHidden>
          </Link>
        </Heading>
        <Text
          as="p"
          marginTop="2"
          color={textColor}
          fontSize={{ base: "md", sm: "lg" }}
          lineHeight="tall"
        >
          {description}
        </Text>
        <BlogAuthor name={authorName} date={authorDate} />
      </Box>
    </Box>
  );
};

const LandingNews = () => {
  const dividerColor = useColorModeValue("gray.300", "gray.600");

  // News articles data for easier management
  const newsArticles = [
    {
      href: "https://geog.umd.edu/featured-content/nasa-honors-research-professor-louis-giglio-prestigious-public-leadership-medal",
      imageSrc: medal,
      imageAlt: "graphic of NASA Outstanding Public Leadership Medal",
      tags: ["Award"],
      title: "NASA Honors Research Professor Louis Giglio With Prestigious Public Leadership Medal",
      description:
        "NASA has recently announced this year's recipients of its Honor Awards, recognizing exceptional contributions across the agency. Among them is Louis Giglio, a research professor at the Department of Geographical Sciences, who has received the Outstanding Public Leadership Medal. This medal honors nongovernment individuals for sustained, high-impact leadership that significantly advances NASA's mission and image. Recipients are recognized for delivering meaningful results, modeling NASA's core values, fostering collaboration and influencing lasting change.",
      authorName: "L. Giglio",
      authorDate: new Date("2025-06-09T19:01:27Z"),
    },
    {
      href: "https://geog.umd.edu/featured-content/associate-research-professor-joanne-hall-leads-global-field-campaigns-track-crop",
      imageSrc: crop_field,
      imageAlt: "Aerial view of agricultural fields with crop residue burning for the NASA CMS project",
      tags: ["Field Campaigns", "Crop Residue Burning"],
      title: "Associate Research Professor Joanne Hall Leads Global Field Campaigns to Track Crop Residue Burning",
      description:
        "Associate Research Professor Joanne Hall organized three crop residue burning field campaigns in summer and fall 2025 as part of her NASA Carbon Monitoring System (CMS) project aimed at improving crop residue burning and emission estimates for the EPA's National Emissions Inventory (NEI).",
      authorName: "J. V. Hall",
      authorDate: new Date("2025-12-11T19:01:27Z"),
    },
  ];

  return (
    <ChakraProvider theme={theme}>
      <Container maxW="7xl" p={{ base: "4", sm: "8", md: "12" }}>
        {/* Main heading for the news section */}
        <Heading
          as="h1"
          size={{ base: "xl", md: "2xl" }}
          marginBottom={{ base: "2", sm: "4" }}
        >
          Latest News
        </Heading>

        {/* News Articles Section */}
        <Box as="section" aria-label="Latest news articles">
          {newsArticles.map((article, index) => (
            <React.Fragment key={article.title}>
              <NewsArticle
                href={article.href}
                imageSrc={article.imageSrc}
                imageAlt={article.imageAlt}
                tags={article.tags}
                title={article.title}
                description={article.description}
                authorName={article.authorName}
                authorDate={article.authorDate}
                headingLevel="h2"
              />
              {index < newsArticles.length - 1 && (
                <Divider
                  marginTop="5"
                  borderColor={dividerColor}
                  aria-hidden="true"
                />
              )}
            </React.Fragment>
          ))}
        </Box>

        <Divider
          marginTop="5"
          borderColor={dividerColor}
          aria-hidden="true"
        />

        {/* Welcome Section */}
        <VStack
          as="section"
          paddingTop={{ base: "6", sm: "10" }}
          spacing="4"
          alignItems="flex-start"
          aria-labelledby="welcome-heading"
        >
          <Heading as="h2" id="welcome-heading" size={{ base: "lg", md: "xl" }}>
            Welcome to the Giglio Fire Group Website
          </Heading>
          <Text as="p" fontSize={{ base: "md", sm: "lg" }} lineHeight="tall">
            Within the Department of Geographical Sciences at the University of
            Maryland our team has a strong track record in the field of satellite
            fire mapping, biomass burning research, and global product generation.
            Our team lead, Dr. Louis Giglio, is the NASA S-NPP and MODIS active
            fire and burned area Principal Investigator and a member of the NASA
            MODIS and S-NPP Science Teams.
          </Text>
          <Text as="p" fontSize={{ base: "md", sm: "lg" }} lineHeight="tall">
            UMD Wildfire team develops active fire detection and burned area
            algorithms for a broad suite of space-borne sensors, produces
            long-term, multi-sensor global fire data sets, validates biomass
            burning data, and conducts high quality research targeted to improve
            our understanding of current global fire activity, its trends and
            future projections, together with its impact on human health,
            biodiversity, and economy.
          </Text>
        </VStack>
      </Container>
    </ChakraProvider>
  );
};

export default LandingNews;